//@import store
import {
  login as loginAction,
  logout as logoutAction,
  updateToken,
} from 'app_events/auth/actions'
import { store } from 'app_storage/redux-storage'
import CryptoJS from 'crypto-js'
//@end

//@import utils
import { Http, HttpCustomStructure } from 'app_utils/http'
import { ScreenModesUtil } from 'app_utils/screenModes'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
//@end

// @import types
import { IUser, IUserHttpResponse, IPasswordRecoveryFromSource, IChangeRecoveredPassword } from 'app_types/userTypes'
// @end

//@import service
import {routingService} from 'app_services/routingService'
import UserService from 'app_services/user/userService'
import RouterService from 'app_services/router/routerService'
//@end

const LOGIN_REDIRECTION_TIME = 300

class AuthService {
  private Http: any
  private storeObject: any
  private screenNotification: ScreenNotificationsUtil
  private screenModes: ScreenModesUtil
  private userService: UserService
  private CREDENTIALS_ENCRYPTED_KEY = 'my-super-secret-123456'
  private CREDENTIALS_LOCATION = 'encrypted-credentials'

  constructor() {
    this.Http = Http
    this.storeObject = store
    this.screenNotification = new ScreenNotificationsUtil()
    this.screenModes = new ScreenModesUtil()
    this.userService = new UserService()
  }

  /**
   * Metodo que permite iniciar sesión
   * @param {username, password} Object Items para inicio de sesión
   * @param history Object Elementos del objeto props
   */
  login = async (credentials: any, history?: any) => {

    let responseData: IUserHttpResponse


    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/auth/login`,
      params: credentials
    }

    responseData = await this.Http.send(queryData)
    if (responseData.code === 200) {
      if (!responseData?.check2fa) {
        return this.processLogin(responseData, history)
      }
    } else {
      this.screenNotification.fire(responseData)
    }

    return responseData
  }

  validate2FA = async (code: string, history: any) => {
    let responseData: IUserHttpResponse

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/auth/confirm-2fa/${code}`
    }

    responseData = await this.Http.send(queryData)
    if (responseData.code === 200) {
      return this.processLogin(responseData, history)
    } else {
      this.screenNotification.fire(responseData)
    }

    return responseData
  }

  /**
   * Metodo que procesa los datos del usuario y ejecuta los metodos de login
   * @param userData
   * @param history
   * @returns
   */
  processLogin = async (userData: IUserHttpResponse, history?: any) => {

    let user: IUser | null = null

    user = {
      token: userData.token,
      data: userData.user,
      timestamp: new Date().getTime(),
      locale: (userData.locale) ? userData.locale : '',
      academicResourceCategories: userData.academicResourceCategories,
      academicResourceConfigCategories: userData.academicResourceConfigCategories,
      questionCategories: userData.questionCategories,
      attachedCategories: userData.attachedCategories,
      selfRegisterEnrollments: userData?.selfRegisterEnrollments,
    }

    await this.storeObject.dispatch(loginAction(user))

    // @INFO: Inicializando idioma segun el usuario
    await this.userService.setI18nCustom(userData)

    if (userData.user?.screen_mode) {
      this.screenModes.setMode(userData.user.screen_mode)
    }
    const routerService = new RouterService()
    const urlParams = routerService.getParamsURLObject()

    setTimeout(() => {
      if (urlParams?.redirect?.trim()?.length) {
        window.location.href = urlParams.redirect
      } else if (window.location.href?.includes('login')) {
        window.location.href = routingService.getSecurePath()
      }
    }, [LOGIN_REDIRECTION_TIME])

    return user
  }

  confirmEmail = async (token: string) => {
    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/admin/user/confirm-email/${token}`
    }

    responseData = await this.Http.send(queryData)

    return responseData
  }

  /**
   * Metodo que permite el cierre de sesión en la aplicación
   */
  logout = () => this.storeObject.dispatch(logoutAction())

  /**
   * Metodo que permite actualizar el token de sesión
   */
  updateToken = (token: string) => {
    this.storeObject.dispatch(updateToken(token))
  }

  /**
   * Metodo que permtie enviar la solicitud de recuperación de contraseña
   * @param params
   * @returns
   */
  passwordRecovery = async (params: IPasswordRecoveryFromSource) => {

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'GET',
      url: `/api/auth/password-recovery`,
      params
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) return responseData
    else this.screenNotification.fire(responseData)

    return null
  }

  /**
   * Metodo que permite el cambio de contraseña
   * @param params
   * @returns
   */
  changeRecoveredPassword = async (params: IChangeRecoveredPassword) => {

    let responseData: any

    const queryData: HttpCustomStructure = {
      method: 'POST',
      url: `/api/auth/change-recovered-password`,
      params
    }

    responseData = await this.Http.send(queryData)

    if (responseData.code === 200) return responseData
    else this.screenNotification.fire(responseData)

    return null
  }

  saveCredentials = (username: string, password: string) => {
    if (!username || !password) return
    try {
      const encryptedData = CryptoJS.AES.encrypt(JSON.stringify({ username, password }), this.CREDENTIALS_ENCRYPTED_KEY)
      localStorage.setItem(this.CREDENTIALS_LOCATION, encryptedData.toString())
    } catch (_) {
      // do-nothing
    }
  }

  getCredentials = (): { username: string, password: string } | undefined => {
    try {
      const encryptedData = localStorage.getItem(this.CREDENTIALS_LOCATION)
      if (!encryptedData) return
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.CREDENTIALS_ENCRYPTED_KEY)
      const credentials = JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
      if (!credentials?.username || !credentials?.password) return
      return credentials
    } catch(_) {
      // do-nothing
    }
  }
}

export default AuthService
